import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, FileDown } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import type { BenefitsHistory } from "@/types/hr";
import { exportBenefitsToPDF } from "@/lib/pdfExport";

interface EmployeeBenefitsTableProps {
  employeeId: string;
  employeeName?: string;
  readOnly?: boolean;
  canViewData?: boolean;
}

// Deutsche Handwerk-Benefits Optionen
const BENEFIT_OPTIONS = [
  { value: "betriebliche_altersvorsorge", label: "Betriebliche Altersvorsorge" },
  { value: "betriebliche_krankenversicherung", label: "Betriebliche Krankenversicherung" },
  { value: "jobrad", label: "JobRad" },
  { value: "firmenwagen", label: "Firmenwagen" },
  { value: "hansefit", label: "Hansefit / Fitnessstudio" },
  { value: "tankgutschein", label: "Tankgutschein" },
  { value: "essenzuschuss", label: "Essenzuschuss" },
  { value: "fahrtkostenzuschuss", label: "Fahrtkostenzuschuss" },
  { value: "weiterbildung", label: "Weiterbildung" },
  { value: "vermoegenswirksame_leistungen", label: "Vermögenswirksame Leistungen" },
  { value: "sonstiges", label: "Sonstiges" },
];

export function EmployeeBenefitsTable({ employeeId, employeeName = "Mitarbeiter", readOnly = false, canViewData: canViewDataProp }: EmployeeBenefitsTableProps) {
  const { user, profile, userRole } = useAuth();
  
  // Berechne canViewData intern wenn nicht explizit gesetzt
  const isAdmin = userRole === 'admin';
  const isOwnProfile = profile?.employee_id === employeeId;
  const canViewData = canViewDataProp !== undefined ? canViewDataProp : (isAdmin || isOwnProfile);
  const [benefitsHistory, setBenefitsHistory] = useState<BenefitsHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<BenefitsHistory>>({
    currency: "EUR",
    status: "active",
    payment_frequency: "monthly",
  });

  useEffect(() => {
    if (user && employeeId) {
      loadBenefitsHistory();
    }
  }, [user, employeeId]);

  const loadBenefitsHistory = async () => {
    try {
      const { data, error } = await supabase
        .from("benefits_history")
        .select("*")
        .eq("employee_id", employeeId)
        .order("start_date", { ascending: false });

      if (error) throw error;
      setBenefitsHistory(data || []);
    } catch (error) {
      console.error("Error loading benefits history:", error);
      toast.error("Fehler beim Laden der Benefits");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      currency: "EUR",
      status: "active",
      payment_frequency: "monthly",
    });
    setDialogOpen(true);
  };

  const handleEdit = (entry: BenefitsHistory) => {
    setEditingId(entry.id);
    setFormData(entry);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.benefit_name || !formData.start_date || !formData.monthly_value) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const { error } = await supabase
          .from("benefits_history")
          .update({
            benefit_type: formData.benefit_type || "sonstiges",
            benefit_name: formData.benefit_name,
            start_date: formData.start_date,
            end_date: formData.end_date,
            monthly_value: formData.monthly_value,
            currency: formData.currency || "EUR",
            payment_frequency: formData.payment_frequency || "monthly",
            provider: formData.provider,
            status: formData.status || "active",
            notes: formData.notes,
          })
          .eq("id", editingId);

        if (error) throw error;
        toast.success("Benefit erfolgreich aktualisiert");
      } else {
        const { error } = await supabase
          .from("benefits_history")
          .insert([{
            employee_id: employeeId,
            benefit_type: formData.benefit_type || "sonstiges",
            benefit_name: formData.benefit_name!,
            start_date: formData.start_date!,
            end_date: formData.end_date,
            monthly_value: formData.monthly_value!,
            currency: formData.currency || "EUR",
            payment_frequency: formData.payment_frequency || "monthly",
            provider: formData.provider,
            status: formData.status || "active",
            notes: formData.notes,
            created_by: user!.id,
          }]);

        if (error) throw error;
        toast.success("Benefit erfolgreich gespeichert");
      }

      setDialogOpen(false);
      loadBenefitsHistory();
    } catch (error) {
      console.error("Error saving benefits history:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return;

    try {
      const { error } = await supabase
        .from("benefits_history")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Eintrag gelöscht");
      loadBenefitsHistory();
    } catch (error) {
      console.error("Error deleting benefits history:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const getBenefitLabel = (benefitName: string) => {
    const option = BENEFIT_OPTIONS.find(opt => opt.value === benefitName);
    return option ? option.label : benefitName;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "outline"> = {
      active: "default",
      inactive: "secondary",
      pending: "outline",
    };
    return <Badge variant={variants[status] || "outline"}>{status === "active" ? "Aktiv" : status === "inactive" ? "Inaktiv" : "Ausstehend"}</Badge>;
  };

  const calculateTotalBenefits = () => {
    return benefitsHistory
      .filter(b => b.status === "active" && !b.end_date)
      .reduce((sum, b) => {
        const value = b.monthly_value || 0;
        const freq = b.payment_frequency || "monthly";
        if (freq === "yearly") return sum + value / 12;
        if (freq === "half_yearly") return sum + value / 6;
        if (freq === "quarterly") return sum + value / 3;
        return sum + value;
      }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const totalBenefits = calculateTotalBenefits();

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Benefits</CardTitle>
              <CardDescription>Verwalten Sie Benefits und Zusatzleistungen</CardDescription>
              {totalBenefits > 0 && canViewData && (
                <p className="text-sm font-semibold mt-2">
                  Gesamtsumme aktive Benefits: {totalBenefits.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} / Monat
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {canViewData && (
                <Button variant="outline" onClick={() => exportBenefitsToPDF(employeeName, benefitsHistory, totalBenefits)}>
                  <FileDown className="mr-2 h-4 w-4" />
                  PDF
                </Button>
              )}
              {!readOnly && (
                <Button onClick={handleAdd}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neues Benefit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {benefitsHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Benefit</TableHead>
                  <TableHead className="min-w-[100px]">Start</TableHead>
                  <TableHead className="min-w-[100px]">Ende</TableHead>
                  <TableHead className="min-w-[100px]">Betrag</TableHead>
                  <TableHead className="min-w-[100px]">Frequenz</TableHead>
                  <TableHead className="min-w-[100px]">Status</TableHead>
                  {!readOnly && <TableHead className="min-w-[80px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {benefitsHistory.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{getBenefitLabel(entry.benefit_name)}</TableCell>
                    <TableCell>
                      {format(new Date(entry.start_date), "dd.MM.yyyy", { locale: de })}
                    </TableCell>
                    <TableCell>
                      {entry.end_date ? format(new Date(entry.end_date), "dd.MM.yyyy", { locale: de }) : "-"}
                    </TableCell>
                    <TableCell>
                      {canViewData ? (
                        entry.monthly_value
                          ? entry.monthly_value.toLocaleString("de-DE", {
                              style: "currency",
                              currency: entry.currency,
                            })
                          : "-"
                      ) : (
                        <span className="text-muted-foreground italic">Vertraulich</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {entry.payment_frequency === "yearly"
                        ? "Jährlich"
                        : entry.payment_frequency === "half_yearly"
                        ? "Halbjährlich"
                        : entry.payment_frequency === "quarterly"
                        ? "Quartalsweise"
                        : "Monatlich"}
                    </TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    {!readOnly && (
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(entry)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(entry.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Noch keine Benefits vorhanden
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Benefit bearbeiten" : "Benefit hinzufügen"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4">
            <div>
              <Label>Benefit auswählen *</Label>
              <Select
                value={formData.benefit_name || ""}
                onValueChange={(value) => setFormData({ ...formData, benefit_name: value, benefit_type: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Benefit auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {BENEFIT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Betrag *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.monthly_value || ""}
                  onChange={(e) => setFormData({ ...formData, monthly_value: parseFloat(e.target.value) })}
                  placeholder="z.B. 50"
                />
              </div>
            <div>
              <Label>Zahlungsfrequenz *</Label>
              <Select
                value={formData.payment_frequency || "monthly"}
                onValueChange={(value: any) => setFormData({ ...formData, payment_frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="quarterly">Quartalsweise</SelectItem>
                  <SelectItem value="half_yearly">Halbjährlich</SelectItem>
                  <SelectItem value="yearly">Jährlich</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Startdatum *</Label>
                <Input
                  type="date"
                  value={formData.start_date || ""}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Enddatum</Label>
                <Input
                  type="date"
                  value={formData.end_date || ""}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={formData.status || "active"}
                  onValueChange={(value: any) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Anbieter</Label>
              <Input
                value={formData.provider || ""}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                placeholder="z.B. Allianz, Deutsche Betriebsrente, etc."
              />
            </div>

            <div>
              <Label>Notizen</Label>
              <Textarea
                rows={3}
                value={formData.notes || ""}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Zusätzliche Informationen..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}